import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"

// The curator Run Analysis button: POSTs the Observatory analysis request, then follows the
// request's lifecycle over VerificationChannel — analysis_accepted (Observatory accepted the
// run), analysis_completed (the scored delivery landed; carries the fresh maturity), and
// analysis_failed. The subscription stays open so the button works repeatedly on one page.
export default class extends Controller {
  static targets = ["analyzeButton", "maturityBadge", "maturityScore", "maturityAssessedAt", "status"]
  static values = { organizationId: String, analyzeUrl: String }

  connect() {
    this.consumer = createConsumer()
    this.subscribe()
  }

  disconnect() {
    this.unsubscribe()
    this.consumer?.disconnect()
  }

  analyze() {
    this.clearStatus()
    this.busy("Requesting…")

    fetch(this.analyzeUrlValue, {
      method: "POST",
      headers: {
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content,
        "Accept": "application/json"
      }
    }).then(response => {
      if (!response.ok) {
        this.resetAnalyzeButton()
        this.showStatus("Request failed")
      }
    }).catch(() => {
      this.resetAnalyzeButton()
      this.showStatus("Request failed")
    })
  }

  subscribe() {
    this.unsubscribe()

    this.subscription = this.consumer.subscriptions.create(
      { channel: "VerificationChannel", organization_id: this.organizationIdValue },
      {
        received: (data) => this.handleMessage(data)
      }
    )
  }

  unsubscribe() {
    this.subscription?.unsubscribe()
    this.subscription = null
  }

  handleMessage(data) {
    if (data.type === "analysis_accepted") this.busy("Analyzing…")
    if (data.type === "analysis_completed") this.onAnalysisCompleted(data)
    if (data.type === "analysis_failed") this.onAnalysisFailed(data)
  }

  busy(labelText) {
    if (!this.hasAnalyzeButtonTarget) return
    const btn = this.analyzeButtonTarget
    btn.disabled = true
    btn.innerHTML = `
      <svg class="w-3 h-3 mr-1 animate-spin" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
      </svg>
      ${labelText}
    `
  }

  resetAnalyzeButton() {
    if (this.hasAnalyzeButtonTarget) {
      this.analyzeButtonTarget.disabled = false
      this.analyzeButtonTarget.innerHTML = `
        <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
        </svg>
        Run Analysis
      `
    }
  }

  showStatus(text) {
    if (this.hasStatusTarget) this.statusTarget.textContent = text
  }

  clearStatus() {
    if (this.hasStatusTarget) this.statusTarget.textContent = ""
  }

  onAnalysisCompleted(data) {
    this.resetAnalyzeButton()
    this.clearStatus()
    if (data.maturity) {
      this.updateMaturity(data.maturity)
    }
  }

  onAnalysisFailed(data) {
    this.resetAnalyzeButton()
    this.showStatus(data.reason ? `Analysis failed: ${data.reason}` : "Analysis failed")
  }

  updateMaturity(maturity) {
    const badgeColors = {
      'Not Present': 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
      'Contact Only': 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
      'Basic VDP': 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
      'Partial Safe Harbor': 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300',
      'Full Safe Harbor': 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
      'Full Safe Harbor + CVD': 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300',
      'Unverified': 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
    }

    if (this.hasMaturityBadgeTarget) {
      const colorClass = badgeColors[maturity.label] || badgeColors['Not Present']
      this.maturityBadgeTarget.className = `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`
      this.maturityBadgeTarget.textContent = `Level ${maturity.level ?? "—"}: ${maturity.label}`
    }

    if (this.hasMaturityScoreTarget) {
      this.maturityScoreTarget.innerHTML = `
        Score: <span class="font-medium text-gray-900 dark:text-white">${maturity.score}</span>
      `
    }

    if (this.hasMaturityAssessedAtTarget) {
      this.maturityAssessedAtTarget.textContent = "Analyzed just now"
    }
  }
}
